import { useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import { uploadBlogImage } from "JS/API";
import {
  BLOG_IMAGE_EXTENSIONS,
  BLOG_ERROR_MESSAGE,
  normalizeErrorDetail,
} from "utils/blogConstants";
import "react-quill/dist/quill.snow.css";

Quill.register("modules/imageResize", ImageResize, true);

// 백엔드 sanitize 허용 태그와 1:1 매칭되는 툴바/포맷
const MODULES_BASE = {
  toolbar: [
    [{ header: [2, 3, false] }], // h2, h3 (h1은 백엔드 허용 안 함)
    ["bold", "italic"], // strong, em
    [{ list: "ordered" }, { list: "bullet" }], // ol, ul
    ["blockquote", "code-block"], // blockquote, pre/code
    ["link", "image", "video"], // a, img, iframe(YouTube)
    ["clean"],
  ],
  imageResize: {
    parchment: Quill.import("parchment"),
    modules: ["Resize", "DisplaySize"],
  },
};

const FORMATS = [
  "header",
  "bold",
  "italic",
  "list",
  "bullet",
  "blockquote",
  "code-block",
  "link",
  "image",
  "video",
  "align",
];

const BlogEditor = ({ value, onChange, onImageError, minHeight = "500px" }) => {
  const quillRef = useRef(null);

  // 파일 확장자 검사
  const isAllowedExtension = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    return BLOG_IMAGE_EXTENSIONS.includes(ext);
  };

  // 이미지 업로드 핸들러 - /upload-image 호출 후 에디터에 삽입
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (!input.files || !input.files[0]) return;
      const file = input.files[0];

      if (!isAllowedExtension(file.name)) {
        onImageError?.(BLOG_ERROR_MESSAGE.invalidImage);
        return;
      }

      try {
        const result = await uploadBlogImage(file);
        if (typeof result !== "object" || !result?.data?.data?.url) {
          onImageError?.(BLOG_ERROR_MESSAGE.uploadFailed);
          return;
        }

        const url = result.data.data.url;
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", url);
        quill.insertText(range.index + 1, "\n");
        quill.setSelection(range.index + 2);
      } catch (err) {
        const detailText = normalizeErrorDetail(err?.response?.data?.detail);
        onImageError?.(detailText || BLOG_ERROR_MESSAGE.uploadFailed);
      }
    };
  };

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");
      toolbar.addHandler("image", imageHandler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="blog-editor-wrap"
      onKeyDown={(e) => {
        if (e.key === "Enter") e.stopPropagation();
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={MODULES_BASE}
        formats={FORMATS}
        value={value || ""}
        onChange={(html) => onChange?.(html)}
        placeholder="본문을 입력하세요. 이미지는 툴바의 이미지 버튼으로 업로드할 수 있습니다."
      />
      <style>{`
        .blog-editor-wrap .ql-container {
          min-height: ${minHeight};
          font-size: 15px;
          line-height: 1.7;
        }
        .blog-editor-wrap .ql-editor {
          min-height: ${minHeight};
        }
        .blog-editor-wrap .ql-editor h2 {
          font-size: 22px;
          font-weight: 700;
          margin: 20px 0 12px;
        }
        .blog-editor-wrap .ql-editor h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 16px 0 10px;
        }
        .blog-editor-wrap .ql-editor img {
          max-width: 100%;
          height: auto;
        }
        .blog-editor-wrap .ql-toolbar {
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background-color: #f8fafc;
        }
        .blog-editor-wrap .ql-container {
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }
      `}</style>
    </div>
  );
};

export default BlogEditor;
